import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/axios';

const BASE = '/api/comm';

export interface CommSettingsView {
  email: {
    configured: boolean;
    dependencyInstalled: boolean;
    host: string;
    port: number;
    secure: boolean;
    user: string;
    fromName: string;
    fromEmail: string;
  };
  whatsapp: {
    configured: boolean;
    provider: 'meta' | 'interakt';
    phoneNumberId: string;
    businessAccountId: string;
    webhookVerifyTokenSet: boolean;
    apiKeySet: boolean;
    /** 'integrations' = sending with the Integrations → Interakt connection. */
    source?: 'integrations' | 'comm' | '';
  };
  connectors: {
    paymentProvider: string;
    paymentConfigured: boolean;
    paymentKeyId: string;
    calendarIcsUrl: string;
    storageProvider: string;
    storageConfigured: boolean;
  };
  canManage: boolean;
}

export interface MessageLogEntry {
  _id: string;
  channel: 'email' | 'whatsapp';
  direction: 'out' | 'in';
  to: string;
  from: string;
  subject: string;
  body: string;
  status: 'sent' | 'failed' | 'received';
  error: string;
  createdAt: string;
}

export interface EmailTemplateView {
  id: string;
  templateKey: string;
  name: string;
  description: string;
  category: 'hr' | 'crm';
  variables: string[];
  subject: string;
  bodyHtml: string;
  active: boolean;
  updatedAt: string;
}


export function useCommSettings() {
  return useQuery({
    queryKey: commKeys.settings,
    queryFn: async () => (await api.get<CommSettingsView>(`${BASE}/settings`)).data,
  });
}

export function useCommLogs(channel: '' | 'email' | 'whatsapp') {
  return useQuery({
    queryKey: commKeys.logs(channel),
    queryFn: async () => {
      const suffix = channel ? `?channel=${channel}` : '';
      return (await api.get<MessageLogEntry[]>(`${BASE}/logs${suffix}`)).data;
    },
  });
}

export function useCommMutations() {
  const queryClient = useQueryClient();
  const refresh = () => queryClient.invalidateQueries({ queryKey: commKeys.root });

  const saveEmailSettings = useMutation({
    mutationFn: async (payload: {
      host: string;
      port: number;
      secure: boolean;
      user: string;
      pass: string;
      fromName: string;
      fromEmail: string;
    }) => (await api.put(`${BASE}/settings/email`, payload)).data,
    onSuccess: refresh,
  });

  const saveWhatsappSettings = useMutation({
    mutationFn: async (payload: {
      provider: 'meta' | 'interakt';
      phoneNumberId: string;
      accessToken: string;
      businessAccountId: string;
      verifyToken: string;
      apiKey: string;
    }) => (await api.put(`${BASE}/settings/whatsapp`, payload)).data,
    onSuccess: refresh,
  });

  const saveConnectorSettings = useMutation({
    mutationFn: async (payload: {
      paymentProvider: string;
      paymentKeyId: string;
      paymentKeySecret: string;
      calendarIcsUrl: string;
      storageProvider: string;
      storageBucket: string;
      storageKeyId: string;
      storageKeySecret: string;
    }) => (await api.put(`${BASE}/settings/connectors`, payload)).data,
    onSuccess: refresh,
  });

  const sendEmail = useMutation({
    mutationFn: async (payload: { to: string; subject: string; body: string }) =>
      (await api.post(`${BASE}/email/send`, payload)).data,
    onSuccess: refresh,
  });

  const sendWhatsapp = useMutation({
    mutationFn: async (payload: { to: string; message: string }) =>
      (await api.post(`${BASE}/whatsapp/send`, payload)).data,
    onSuccess: refresh,
  });

  return { saveEmailSettings, saveWhatsappSettings, saveConnectorSettings, sendEmail, sendWhatsapp };
}

// ── Email templates ─────────────────────────────────────────────────────────
//
// These read /api/email-templates, which is the store the SENDING code uses
// (src/api-email-templates.js, collection `email_templates`, seeded with seven
// defaults on first list). They used to read /api/notification-templates — a
// different, unseeded collection that nothing sends from, so the tab was
// permanently empty and anything edited there would never have reached a
// customer. A template edited here is the one that goes out.
export const commKeys = {
  root: ['comm'] as const,
  settings: ['comm', 'settings'] as const,
  logs: (channel: string) => ['comm', 'logs', channel] as const,
  templates: ['comm', 'templates'] as const,
};

const TEMPLATES_BASE = '/api/email-templates';

/** Accepts a bare name or a '{{wrapped}}' one; the UI lists bare names. */
function normalizeVariable(value: string): string {
  return String(value || '').replace(/^\{\{/, '').replace(/\}\}$/, '').trim();
}

/** The API's own document shape; the view below is nearly identical. */
interface EmailTemplateRecord {
  _id: string;
  templateKey: string;
  name: string;
  description?: string;
  category?: 'hr' | 'crm';
  subject: string;
  bodyHtml: string;
  variables?: string[];
  active?: boolean;
  updatedAt?: string;
}

function toEmailTemplateView(record: EmailTemplateRecord): EmailTemplateView {
  return {
    id: String(record._id),
    templateKey: String(record.templateKey || ''),
    name: String(record.name || record.templateKey || 'Untitled template'),
    description: String(record.description || ''),
    category: record.category === 'hr' ? 'hr' : 'crm',
    variables: Array.isArray(record.variables) ? record.variables.map(normalizeVariable) : [],
    subject: String(record.subject || ''),
    bodyHtml: String(record.bodyHtml || ''),
    active: Boolean(record.active ?? true),
    updatedAt: String(record.updatedAt || ''),
  };
}

export function useEmailTemplates() {
  return useQuery({
    queryKey: commKeys.templates,
    queryFn: async () => {
      const { data } = await api.get<EmailTemplateRecord[]>(TEMPLATES_BASE);
      return data.map(toEmailTemplateView);
    },
  });
}

export function useUpdateEmailTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      key: string;
      subject: string;
      bodyHtml: string;
      name?: string;
      description?: string;
      category?: 'hr' | 'crm';
      variables?: string[];
    }) => {
      // The server compiles both subject and body as Handlebars before saving
      // and refuses a template that will not compile — so a broken {{variable}}
      // is caught here rather than at the moment an email fails to send.
      const { data } = await api.put<EmailTemplateRecord>(`${TEMPLATES_BASE}/${payload.key}`, {
        subject: payload.subject,
        bodyHtml: payload.bodyHtml,
        name: payload.name,
        description: payload.description,
        category: payload.category,
        variables: payload.variables,
      });
      return toEmailTemplateView(data);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: commKeys.templates }),
  });
}

export function useToggleEmailTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (templateKey: string) => {
      const { data } = await api.patch<EmailTemplateRecord>(`${TEMPLATES_BASE}/${templateKey}/toggle`, {});
      return toEmailTemplateView(data);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: commKeys.templates }),
  });
}

export function usePreviewEmailTemplate() {
  return useMutation({
    // Rendered by the server with the real Handlebars runtime and the real
    // company context, so the preview is what would actually be sent. The old
    // one did a regex replace in the browser, which showed neither.
    mutationFn: async ({ key, sampleData }: { key: string; sampleData?: Record<string, string> }) => {
      const { data } = await api.post<{ subject: string; html: string }>(
        `${TEMPLATES_BASE}/${key}/preview`,
        sampleData || {},
      );
      return data;
    },
  });
}
